import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import "./CareersHero.css";
import { FaArrowDown } from "react-icons/fa";
import { resolveMediaUrl } from "../../../../utils/media";
import heroFallback from "../../../../assets/images/careers/careersHero.png";
import { scrollToElementById } from "../../../../utils/scrollToId";

const tryParseJsonObject = (str) => {
  const t = String(str || "").trim();
  if (!t.startsWith("{") || !t.endsWith("}")) return null;
  try {
    const o = JSON.parse(t);
    return o && typeof o === "object" && !Array.isArray(o) ? o : null;
  } catch {
    return null;
  }
};

const coalesceHeroPayload = (raw) => {
  if (raw == null) return {};
  if (typeof raw === "string") {
    return coalesceHeroPayload(tryParseJsonObject(raw) || {});
  }
  if (typeof raw !== "object") return {};

  const mergeHeroWrapper = (node, into) => {
    const wrap = node?.hero ?? node?.Hero;
    if (!wrap || typeof wrap !== "object") return into;
    if (wrap.data && typeof wrap.data === "object" && !Array.isArray(wrap.data)) {
      return { ...into, ...wrap.data };
    }
    return { ...into, ...wrap };
  };

  let base = { ...raw };
  base = mergeHeroWrapper(raw, base);
  if (raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)) {
    base = { ...base, ...raw.data };
    base = mergeHeroWrapper(raw.data, base);
  }
  if (typeof raw.value === "string") {
    const parsed = tryParseJsonObject(raw.value);
    if (parsed) base = { ...base, ...parsed };
  } else if (raw.value && typeof raw.value === "object" && !Array.isArray(raw.value)) {
    base = { ...base, ...raw.value };
  }
  return mergeHeroWrapper(base, base);
};

const pickString = (...candidates) => {
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c.trim();
  }
  return "";
};

const pickHeroImage = (p) => {
  if (!p || typeof p !== "object") return "";
  const candidates = [
    p.heroImage,
    p.mainImage,
    p.image,
    p.bannerImage,
    p.mediaUrl,
    p.MediaUrl,
    p.photo,
  ];
  for (const c of candidates) {
    const url = resolveMediaUrl(c);
    if (url) return url;
  }
  return "";
};

const CareersHero = ({ content }) => {
  const { t } = useTranslation();

  const view = useMemo(() => {
    const payload = coalesceHeroPayload(content);
    const imageSrc = pickHeroImage(payload) || heroFallback;
    return {
      tag:
        pickString(payload.tag, payload.sectionTag) ||
        t("sections.careers.hero.tag"),
      title:
        pickString(payload.title, payload.heading, payload.titleLine1) ||
        t("sections.careers.hero.title"),
      description:
        pickString(payload.description, payload.body, payload.text, payload.summary) ||
        t("sections.careers.hero.description"),
      ctaText:
        pickString(payload.ctaText, payload.buttonText, payload.primaryButtonText) ||
        t("sections.careers.hero.ctaText"),
      imageSrc,
    };
  }, [content, t]);

  const handleViewRoles = () => {
    scrollToElementById("careers-open-positions");
  };

  return (
    <section className="careers-hero">
      <div className="careers-container">
        <div className="careers-left">
          {view.tag ? <span className="careers-tag tag">{view.tag}</span> : null}
          {view.title ? <h1>{view.title}</h1> : null}
          {view.description ? <p>{view.description}</p> : null}
          {view.ctaText ? (
            <button type="button" className="careers-btn" onClick={handleViewRoles}>
              {view.ctaText} <FaArrowDown />
            </button>
          ) : null}
        </div>

        {view.imageSrc ? (
          <div className="careers-right">
            <img
              src={view.imageSrc}
              alt=""
              onError={(e) => {
                if (e.currentTarget.src !== heroFallback) {
                  e.currentTarget.src = heroFallback;
                }
              }}
            />
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default CareersHero;
